"use client"

import { useState, useEffect } from 'react'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Button } from '@/components/ui/button'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { useToast } from '@/hooks/use-toast'
import { MessageSquare, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/lib/stores/auth-store'
import { getRedmineBaseUrl } from '@/lib/tenant-config'

interface ReplyNewsSidebarProps {
  isOpen: boolean
  article: any
  onClose: () => void
  onSuccess: () => void
}

export function ReplyNewsSidebar({ isOpen, article, onClose, onSuccess }: ReplyNewsSidebarProps) {
  const [content, setContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { session } = useAuthStore()
  const { toast } = useToast()

  useEffect(() => setMounted(true), [])

  useEffect(() => setContent(''), [article])

  const getAuthHeaders = () => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (session?.credentials?.basicAuth) headers['Authorization'] = `Basic ${session.credentials.basicAuth}`
    else if (session?.apiKey) headers['X-Redmine-API-Key'] = session.apiKey
    headers['X-Redmine-Base-URL'] = getRedmineBaseUrl()
    return headers
  }

  const handleSubmit = async (e?: any) => {
    e?.preventDefault()
    if (!article) return
    
    if (!content.trim()) {
      toast({
        variant: 'destructive',
        title: 'Comment Required',
        description: 'Please enter a comment before posting',
      })
      return
    }

    try {
      setSubmitting(true)
      const headers = getAuthHeaders()

      // Use server-side proxy for authentication consistency
      const res = await fetch(`/api/news/${article.id}/comment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ comment: { content } })
      })

      if (!res.ok) {
        const errorData = await res.json()
        
        // Handle different error types
        if (res.status === 401) {
          toast({
            variant: 'destructive',
            title: 'Authentication Failed',
            description: 'You are not authenticated. Please log in and try again.',
          })
        } else if (res.status === 403) {
          toast({
            variant: 'destructive',
            title: 'Permission Denied',
            description: 'You do not have permission to comment on this news post.',
          })
        } else if (res.status === 404) {
          toast({
            variant: 'destructive',
            title: 'News Not Found',
            description: 'The news post could not be found.',
          })
        } else if (res.status === 422) {
          toast({
            variant: 'destructive',
            title: 'Validation Error',
            description: errorData.details || errorData.error || 'Please check your input and try again.',
          })
        } else {
          toast({
            variant: 'destructive',
            title: 'Failed to Post Comment',
            description: errorData.error || errorData.details || 'An unexpected error occurred. Please try again.',
          })
        }
        return
      }

      // Success
      toast({
        title: 'Comment Posted',
        description: 'Your comment has been added successfully.',
      })

      setContent('')
      onSuccess()
      onClose()
    } catch (e) {
      console.error('Comment post error:', e)
      toast({
        variant: 'destructive',
        title: 'Error Posting Comment',
        description: e instanceof Error ? e.message : 'Failed to post comment',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!mounted) return null

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={onClose}
      title="Reply to News"
      subtitle={article?.title || 'Add your comment'}
      icon={<MessageSquare className="h-5 w-5" />}
      width="md"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Posting...
              </>
            ) : (
              'Post Reply'
            )}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-2">
        <MarkdownEditor value={content} onChange={setContent} height={300} />
      </form>
    </GlobalSidebar>
  )
}
